// Shared admin void (F1 discipline). One implementation used by BOTH the Orders
// tab (AdminOrdersTab.submitCancel) and the Commission tab Delete button, so the
// refund / email / mirror logic can never drift between the two.
//
// Rule Zero: the void is persisted to Supabase FIRST (cancelPortalOrder, awaited).
// If that fails nothing else happens: no refund, no email, no local mirror.
// Nothing is ever erased: the order row is only flipped to cancelled.
//
// F1: every input the refund + email logic needs is copied BY VALUE (snapshotForRefund
// + refundPlan) before the first await. The admin's own realtime echo can flip the
// live cache object to cancelled while cancelPortalOrder is awaiting, so `inv` must
// NOT be read for refund decisions after that point.
import { SHARED_INVOICES } from "./RepPortal";
import { cancelPortalOrder, insertOrderRemoval } from "./supabase";
import { emailOrderCancelled } from "./email";
import { applyLocalRemovalEvent } from "./adminOrderRemovals";
import { validateVoidReason, snapshotForRefund, refundPlan } from "./orderRemovals";
import { isBlockedStatus, normStatus } from "./accountLifecycle";

const defaultDeps = {
  SHARED_INVOICES, cancelPortalOrder, insertOrderRemoval, emailOrderCancelled, applyLocalRemovalEvent,
  fetch: (...a) => fetch(...a),
};

/**
 * True when the void REACHED the database, even if a later step (mirror, email,
 * remove) threw and performAdminVoid returned ok:false. Callers must then treat the
 * invoice as voided (record the commission correction, show a partial-success
 * notice) and never say "Cancellation failed".
 */
export function voidedInDb(res) {
  return !!(res && (res.ok || (res.dbRes && res.dbRes.ok)));
}

/** True when the void was saved on this device only (no database row). */
export function voidLocalOnly(res) {
  return !!(res && (res.localOnly || (res.dbRes && res.dbRes.localOnly)));
}

// Plain-data snapshot of an invoice for the order_removals audit row.
export function removalSnapshotOf(inv, prevStatus) {
  return {
    id: inv.id, orderId: inv.orderId || null, customer: inv.customer || null, total: Number(inv.total || 0),
    status: prevStatus || inv.status || null, rep: inv.rep || null, date: inv.date || null,
    cancelReason: inv.cancelReason || null, cancelledDate: inv.cancelledDate || null,
  };
}

/**
 * @param {object} p
 *   inv          live SHARED_INVOICES object (only read BY VALUE up front)
 *   reason,note  void reason (required list) + optional note
 *   acct         dealer account (or undefined) for the cancellation email
 *   accountName  display account name (fallback for the email name)
 *   displayId    fallback order id for the email
 *   displayAmount fallback amount for the email
 *   alsoRemove, removeReason, adminEmail  optional "also remove from Orders list"
 * @returns {Promise<{ok:boolean, error?:string, validation?:boolean, dbRes?:object,
 *   refundStatus?:string, alreadyVoidedElsewhere?:boolean, localOnly?:boolean,
 *   notices:string[], noticeText:string}>}
 */
export async function performAdminVoid(p, deps = defaultDeps) {
  const d = { ...defaultDeps, ...deps };
  const { inv, reason, note, acct, accountName, displayId, displayAmount, alsoRemove, removeReason, adminEmail } = p || {};
  const fail = (error, extra) => ({ ok: false, error, notices: [], noticeText: "", ...(extra || {}) });
  if (!inv) return fail("Invoice not found");
  const reasonErr = validateVoidReason(reason, note);
  if (reasonErr) return fail(reasonErr, { validation: true });
  if (alsoRemove && !String(removeReason || "").trim()) {
    return fail("Please enter a reason for removing the order from the list, or untick the remove option.", { validation: true });
  }
  const invId = inv.id;
  // Captured BEFORE any mutation: SHARED_INVOICES.cancel mutates this same object.
  const prevStatus = inv.status;
  const preSnapshot = removalSnapshotOf(inv, prevStatus);
  const snap = snapshotForRefund(inv);
  const plan = refundPlan(snap);
  const invRef = inv.id;
  const orderRef = inv.orderId || "";
  // Belt and braces: applyServerState skips this invoice until we are done.
  d.SHARED_INVOICES.beginAdminVoid(snap.id);
  let seenDbRes = null;
  try {
    // STEP 1 (Rule Zero): persist the void to Supabase FIRST.
    const dbRes = await d.cancelPortalOrder({ invoiceRef: invRef, orderRef, reason, note });
    seenDbRes = dbRes;
    if (!dbRes.ok) {
      console.warn("[performAdminVoid] void not saved to Supabase for", invRef, ":", dbRes.reason);
      return fail("The void could not be saved to the database, so NOTHING was changed (no refund, no email). " + (dbRes.reason || "Please try again."), { dbRes });
    }
    // Someone else (another admin, or an approved rep void) already voided this
    // order. Do NOT refund or email a second time; flag it for a manual check.
    const alreadyVoidedElsewhere = !!dbRes.alreadyCancelled;
    // Snapshot-only from here on (F1): never re-read `inv` for refund logic.
    let refundStatus = "not_applicable";
    let refundTransactionId = null;
    const refundLast4 = snap.paidLast4 || null;
    const refundMethod = snap.paidMethod || null;

    if (alreadyVoidedElsewhere) {
      refundStatus = plan === "not_applicable" ? "not_applicable" : "manual";
    } else if (plan === "refund") {
      try {
        const res = await d.fetch("/api/refund-payment", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            paymentId: snap.paymentId || snap.transactionId,
            amount: Math.round((snap.total || 0) * 100), // Square uses cents
            currency: "USD",
            reason,
            invoiceId: snap.id,
            last4: snap.paidLast4,
          }),
        });
        const json = await res.json();
        if (res.ok && json.success) {
          refundStatus = "refunded";
          refundTransactionId = json.refundId;
        } else {
          refundStatus = "failed";
          console.error("Refund failed:", json.error);
        }
      } catch (err) {
        refundStatus = "failed";
        console.error("Refund request error:", err);
      }
    } else if (plan === "manual") {
      refundStatus = "manual";
    }

    d.SHARED_INVOICES.cancel(invId, { reason, note, refundStatus, refundTransactionId, refundLast4, refundMethod });

    // A deleted / paused dealer cannot sign in: skip the customer email and say so (refund logic unchanged).
    const acctBlocked = !!acct && isBlockedStatus(acct.status);
    if (acct?.email && !alreadyVoidedElsewhere && !acctBlocked) {
      d.emailOrderCancelled({
        customerName: acct.contact || acct.company || accountName,
        customerEmail: acct.email,
        invoiceId: snap.id,
        orderId: snap.orderId || displayId,
        total: (snap.total || displayAmount || 0).toFixed(2),
        reason,
        note,
        refundStatus,
        refundTransactionId,
        refundLast4,
        refundMethod,
      }).catch(err => console.error("Cancellation email failed:", err));
    }

    // Optional: also remove from the Orders list (void already succeeded).
    let removeFailMsg = "";
    if (alsoRemove) {
      try {
        const rres = await d.insertOrderRemoval({
          invoiceRef: invRef, orderRef, action: "remove",
          reason: removeReason, actor: adminEmail || "", snapshot: preSnapshot,
          prevStatus, cachedStatus: "cancelled",
        });
        if (rres.ok) d.applyLocalRemovalEvent(rres.event);
        else removeFailMsg = rres.reason || "unknown error";
      } catch (e) {
        console.warn("[performAdminVoid] remove-after-void failed:", e?.message || e);
        removeFailMsg = e?.message || "unknown error";
      }
    }
    const notices = [];
    if (alreadyVoidedElsewhere) notices.push(`Order ${snap.id} had already been voided elsewhere (another admin or an approved rep void). No new refund or customer email was sent. If it was paid by card/ACH, verify the refund in Square / accounting.`);
    if (acctBlocked && !alreadyVoidedElsewhere) notices.push(`The customer account for order ${snap.id} is ${normStatus(acct.status)}, so no cancellation email was sent to the customer.`);
    if (dbRes.localOnly) notices.push(`No database record for order ${snap.id} was visible to this account, so this void is saved on THIS device only. Other devices (rep, dealer, other admins) will not see it. If you expected a database record, ask a developer to check access rules before relying on this void.`);
    if (removeFailMsg) notices.push(`Order ${snap.id} was voided, but it could NOT be removed from the list: ${removeFailMsg}`);
    return { ok: true, dbRes, refundStatus, alreadyVoidedElsewhere, localOnly: !!dbRes.localOnly, notices, noticeText: notices.join(" ") };
  } catch (err) {
    console.error("Cancel order error:", err);
    // dbRes tells the caller whether the void had already reached Supabase before the throw.
    return fail(err?.message || "Cancellation failed", { dbRes: seenDbRes });
  } finally {
    d.SHARED_INVOICES.endAdminVoid(snap.id);
  }
}
